import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import axios from 'axios'
import { ApiResult } from 'src/Libs/api'
import { UnexpectedApiResponseError } from 'src/Libs/errors'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { AuthFetchWithResponse } from 'src/Types/api'
import { DynamicPricesDto, DynamicPricesModel, EnergyDirection, ReferencePriceDto, ReferencePriceModel } from '../types'
import {
  fromDynamicPricesDtoToModel,
  fromDynamicPricesModelFullVersionToDto,
  fromDynamicPricesModelMinimalVersionToDto,
  fromReferencePriceDtoToModel,
  fromReferencePriceModelToDto,
} from './dto/dynamic-price-settings.dto'
import { isEmpty } from 'lodash'

interface ApiResponseError {
  statusCode: StatusCodes
  errno: number
  error: string
}

/**
 * Fetches various dynamic power price settings.
 *
 * @param storageId
 * @param authFetch
 * @param enabled
 * @returns
 */
export const getDynamicPricesSettings = async (
  storageId: string | undefined,
  authFetch: AuthFetchWithResponse,
  enabled: boolean
): Promise<ApiResult<DynamicPricesModel, boolean>> => {
  if (!enabled || !storageId) return new ApiResult<DynamicPricesModel, boolean>(null, false, false)
  try {
    const response = await authFetch<DynamicPricesDto>({
      path: `storages/${storageId}/dynamic-power-price`,
    })
    if (response.status === StatusCodes.OK || response.status === StatusCodes.NO_CONTENT) {
      const mappedResponse: DynamicPricesModel = fromDynamicPricesDtoToModel(response.data)
      return new ApiResult<DynamicPricesModel, boolean>(mappedResponse, false, false)
    }
    return new ApiResult<DynamicPricesModel, boolean>(null, true, false)
  } catch (error) {
    const defaultError = new ApiResult<DynamicPricesModel, boolean>(null, true, false)
    if (!axios.isAxiosError(error)) {
      return defaultError
    }

    const err = error as AuthFetchError

    if (err.response?.status === StatusCodes.BAD_REQUEST && (err.response?.data as ApiResponseError).errno === FetchErrorCodes.NotSupported) {
      return new ApiResult<DynamicPricesModel, boolean>(null, true, true)
    }

    return defaultError
  }
}

/**
 * Fetch reference price settings. Data are send as object of arrays.
 * Thus, one can have multiple reference prices (history).
 */
export const getReferencePriceSettings = async (
  storageId: string | undefined,
  authFetch: AuthFetchWithResponse,
  energyDirection = EnergyDirection.Charge
): Promise<ApiResult<ReferencePriceModel, boolean>> => {
  if (!storageId) return new ApiResult<ReferencePriceModel, boolean>(null, false, false)

  try {
    const response = await authFetch<ReferencePriceDto>({
      path: `storages/${storageId}/prices/${energyDirection}`,
    })

    if (response.status === StatusCodes.OK) {
      if (isEmpty(response.data)) return new ApiResult<ReferencePriceModel, boolean>(null, false, false)
      const mappedResponse: ReferencePriceModel = fromReferencePriceDtoToModel(response.data ?? [])
      return new ApiResult<ReferencePriceModel, boolean>(mappedResponse, false, false)
    } else if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<ReferencePriceModel, boolean>(null, false, false)
    }

    return new ApiResult<ReferencePriceModel, boolean>(null, true, false)
  } catch (error) {
    const defaultError = new ApiResult<ReferencePriceModel, boolean>(null, true, false)
    if (!axios.isAxiosError(error)) {
      return defaultError
    }

    const err = error as AuthFetchError

    if (err.response?.status === StatusCodes.BAD_REQUEST && (err.response?.data as ApiResponseError).errno === FetchErrorCodes.NotSupported) {
      return new ApiResult<ReferencePriceModel, boolean>(null, true, true)
    }

    return defaultError
  }
}

/**
 * Updates the dynamic power prices settings.
 * Some targets still have an older software version for which a reduced payload is used.
 */
export const updateDynamicPricesSettings = async (
  storageId: string | undefined,
  newValues: DynamicPricesModel,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<DynamicPricesModel, boolean>> => {
  const isFullVersion = !!newValues.priceLimitBattery
  const payload = isFullVersion ? fromDynamicPricesModelFullVersionToDto(newValues) : fromDynamicPricesModelMinimalVersionToDto(newValues)
  const response = await authFetch<void>({
    path: `storages/${storageId}/dynamic-power-price`,
    method: 'PUT',
    payload,
  })
  if (response.status !== StatusCodes.NO_CONTENT) {
    throw new UnexpectedApiResponseError(response.status)
  }
  return new ApiResult<DynamicPricesModel, boolean>(newValues, false, false)
}

/**
 * Updates the reference price.
 */
export const updateReferencePriceSettings = async (
  storageId: string,
  ReferencePrice: string | undefined,
  energyDirection = EnergyDirection.Charge,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<void, boolean>> => {
  const payload = fromReferencePriceModelToDto(ReferencePrice)
  const response = await authFetch<void>({
    path: `storages/${storageId}/prices/${energyDirection}`,
    method: 'POST',
    payload,
  })
  if (response.status !== StatusCodes.CREATED) {
    throw new UnexpectedApiResponseError(response.status)
  }
  return new ApiResult<void, boolean>(undefined, false, false)
}
